import { ArrowUp, Files, ListChecks, SlidersHorizontal, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';
import { Card, CardContent } from '~/components/ui/card';
import type { BatchTopMetric } from '~/features/catalog-operations/types/catalog-operations.types';

const ICONS: LucideIcon[] = [TrendingUp, ListChecks, Files, SlidersHorizontal];

interface Props {
  metrics: BatchTopMetric[];
}

export function BatchTopMetrics({ metrics }: Props) {
  const { t } = useTranslation('catalog-operations');

  return (
    <div
      data-testid="batch-detail-top-metrics"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      {metrics.map((metric, i) => {
        const Icon = ICONS[i % ICONS.length] ?? TrendingUp;
        return (
          <Card
            key={metric.label}
            data-testid={`batch-detail-top-metric-card-${String(i)}`}
            className="shadow-stat-card"
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="bg-primary/10 text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                <Icon className="h-5 w-5" aria-hidden="true" strokeWidth={2} />
              </div>
              <div className="min-w-0 space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <span>{metric.label}</span>
                  {metric.tooltip && (
                    <InfoTooltip
                      content={metric.tooltip}
                      ariaLabel={t('batchTopMetrics.infoLabel', { label: metric.label })}
                      testId={`batch-detail-top-metric-card-${String(i)}-info-tooltip`}
                    />
                  )}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <p
                    data-testid={`batch-detail-top-metric-value-${String(i)}`}
                    className="text-2xl font-bold tracking-tight tabular-nums"
                  >
                    {metric.value}
                  </p>
                  {metric.badge && (
                    <span
                      data-testid={`batch-detail-top-metric-badge-${String(i)}`}
                      className="inline-flex items-center gap-0.5 rounded-md border border-green-200 bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700"
                    >
                      <ArrowUp className="h-3 w-3" aria-hidden="true" />
                      {metric.badge}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
